<template>
  <div class="project-bank" v-if="project">
    <div class="accordion" role="tablist">
      <!-- Kunde -->
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-bank-customer
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-user mr-2"></i>{{ $t('bankSectionCustomer') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-bank-customer" role="tabpanel" visible>
          <div class="accordion-content px-2 py-2">
            <div class="bank-section-grid bank-section-grid--paired">
              <div class="label">{{ $t('bankProspectiveCustomer') }}</div>
              <div>
                <b-form-input
                  id="bankProspectiveCustomer"
                  v-model="bank.prospectiveCustomer"
                  type="text"
                  :placeholder="$t('bankProspectiveCustomer')"
                />
              </div>
              <div class="label">{{ $t('bankGbrStatus') }}</div>
              <div>
                <b-form-select
                  id="bankGbrStatus"
                  v-model="bank.gbrStatus"
                  :options="gbrStatusOptions"
                />
              </div>

              <div class="label">{{ $t('bankAttachmentBusinessRegistration') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_BUSINESS_REGISTRATION"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentGbrContract') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_GBR_CONTRACT"
                />
              </div>
            </div>
          </div>
        </b-collapse>
      </div>

      <!-- Selbstauskunft -->
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-bank-self-disclosure
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-file-text-o mr-2"></i>{{ $t('bankSectionSelfDisclosure') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-bank-self-disclosure" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <p class="bank-section-intro mb-2">{{ $t('bankSelfDisclosureInstructions') }}</p>
            <div class="bank-section-grid bank-section-grid--paired">
              <div class="label">{{ $t('bankSelfDisclosureDownload') }}</div>
              <div>
                <a :href="BANK_SELF_DISCLOSURE_TEMPLATE_PATH" download target="_blank">
                  <i class="fa fa-download mr-2"></i>{{ $t('bankSelfDisclosureDownload') }}
                </a>
              </div>
              <div class="label">{{ $t('bankAttachmentSelfDisclosure') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_SELF_DISCLOSURE"
                />
              </div>
            </div>
          </div>
        </b-collapse>
      </div>

      <!-- Einkommen / Lohn / Gehalt -->
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-bank-employment-income
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-money mr-2"></i>{{ $t('bankSectionEmploymentIncome') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-bank-employment-income" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <div class="bank-section-grid bank-section-grid--paired">
              <div class="label">{{ $t('bankAttachmentIncomeTaxAssessment') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_INCOME_TAX_ASSESSMENT"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentIncomeTaxReturn') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_INCOME_TAX_RETURN"
                />
              </div>

              <div class="label">{{ $t('bankAttachmentDecemberPayslip') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_DECEMBER_PAYSLIP"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentCurrentPayslip') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_CURRENT_PAYSLIP"
                />
              </div>
            </div>
          </div>
        </b-collapse>
      </div>

      <!-- Einkünfte aus selbst. Tätigkeit -->
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-bank-self-employment
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-briefcase mr-2"></i>{{ $t('bankSectionSelfEmployment') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-bank-self-employment" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <div class="bank-section-grid bank-section-grid--paired">
              <div class="label">{{ $t('bankSelfEmploymentNetIncome') }}</div>
              <div>
                <InputNumber
                  id="bankSelfEmploymentNetIncome"
                  v-model="bank.selfEmploymentNetIncome"
                  mode="decimal"
                  :minFractionDigits="2"
                  :maxFractionDigits="2"
                  suffix=" €"
                  locale="de-DE"
                  :placeholder="$t('bankSelfEmploymentNetIncome')"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentBwaSusaFullYear') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_BWA_SUSA_FULL_YEAR"
                />
              </div>

              <div class="label">{{ $t('bankAttachmentAnnualFinancialStatement') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_ANNUAL_FINANCIAL_STATEMENT"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentCurrentBwa') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_CURRENT_BWA"
                />
              </div>
            </div>
          </div>
        </b-collapse>
      </div>

      <!-- Mieteinnahmen -->
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-bank-rental-income
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-home mr-2"></i>{{ $t('bankSectionRentalIncome') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-bank-rental-income" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <div class="bank-section-grid bank-section-grid--paired">
              <div class="label">{{ $t('bankHasRentalIncome') }}</div>
              <div>
                <b-form-select
                  id="bankHasRentalIncome"
                  v-model="bank.hasRentalIncome"
                  :options="yesNoOptions"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentRentalIncomeStatements') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_RENTAL_INCOME_STATEMENTS"
                />
              </div>
            </div>
          </div>
        </b-collapse>
      </div>

      <!-- Kapitalerträge -->
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-bank-capital-assets
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-line-chart mr-2"></i>{{ $t('bankSectionCapitalAssets') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-bank-capital-assets" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <div class="bank-section-grid bank-section-grid--paired">
              <div class="label">{{ $t('bankDeclaredAssets') }}</div>
              <div>
                <InputNumber
                  id="bankDeclaredAssets"
                  v-model="bank.declaredAssets"
                  mode="decimal"
                  :minFractionDigits="2"
                  :maxFractionDigits="2"
                  suffix=" €"
                  locale="de-DE"
                  :placeholder="$t('bankDeclaredAssets')"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentLiabilitiesOverview') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_LIABILITIES_OVERVIEW"
                />
              </div>

              <div class="label">{{ $t('bankAttachmentGiroAccountStatement') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_GIRO_ACCOUNT_STATEMENT"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentOverdraftStatement') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_OVERDRAFT_STATEMENT"
                />
              </div>

              <div class="label">{{ $t('bankAttachmentSavingsAccountStatement') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_SAVINGS_ACCOUNT_STATEMENT"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentConsumerLoanStatement') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_CONSUMER_LOAN_STATEMENT"
                />
              </div>

              <div class="label">{{ $t('bankAttachmentSecuritiesDepotStatement') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_SECURITIES_DEPOT_STATEMENT"
                />
              </div>
              <div class="label">{{ $t('bankAttachmentRealEstateLoanStatement') }}</div>
              <div>
                <BankAttachmentUpload
                  :project="project"
                  :attachmentType="BANK_ATTACHMENT_TYPES.BANK_REAL_ESTATE_LOAN_STATEMENT"
                />
              </div>
            </div>
          </div>
        </b-collapse>
      </div>

      <!-- Zusammenfassung -->
      <div class="mb-3">
        <b-button
          block
          v-b-toggle.project-bank-summary
          variant="outline-primary"
          class="text-left accordion-header"
        >
          <i class="fa fa-file-archive-o mr-2"></i>{{ $t('bankSectionSummary') }}
          <i class="fa fa-chevron-down float-right mt-1"></i>
        </b-button>
        <b-collapse id="project-bank-summary" role="tabpanel">
          <div class="accordion-content px-2 py-2">
            <p>
              {{ $t('combinedBankAttachmentsZip') }}
              <ZipDownload
                :attachmentType="BANK_GENERATED_ATTACHMENT_TYPES.BANK_ATTACHMENTS_ZIP"
                :attachments="bankZipAttachments"
                :project="project"
                :dynamic="true"
              />
            </p>
          </div>
        </b-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import InputNumber from 'primevue/inputnumber';
import BankAttachmentUpload from '@/components/Projects/BankAttachmentUpload';
import ZipDownload from '@/components/ZipDownload';
import {
  BANK_ATTACHMENT_TYPES,
  BANK_GENERATED_ATTACHMENT_TYPES,
  BANK_SELF_DISCLOSURE_TEMPLATE_PATH,
  GBR_STATUS,
} from '@/helpers/bankAttachmentTypes';

export default {
  name: 'ProjectBank',
  components: {
    InputNumber,
    BankAttachmentUpload,
    ZipDownload,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      BANK_ATTACHMENT_TYPES,
      BANK_GENERATED_ATTACHMENT_TYPES,
      BANK_SELF_DISCLOSURE_TEMPLATE_PATH,
    };
  },
  computed: {
    bank() {
      return this.project.bank;
    },
    bankZipAttachments() {
      const bankAttachments = this.project?.bank?.attachments;
      if (!bankAttachments) {
        return [];
      }
      return bankAttachments.filter(
        (attachment) =>
          attachment.attachmentType === BANK_GENERATED_ATTACHMENT_TYPES.BANK_ATTACHMENTS_ZIP
      );
    },
    gbrStatusOptions() {
      return [
        { value: GBR_STATUS.FOUNDED, text: this.$t('bankGbrStatus_FOUNDED') },
        { value: GBR_STATUS.TO_BE_FOUNDED, text: this.$t('bankGbrStatus_TO_BE_FOUNDED') },
        { value: GBR_STATUS.NONE, text: this.$t('bankGbrStatus_NONE') },
      ];
    },
    yesNoOptions() {
      return [
        { value: null, text: '' },
        { value: 'YES', text: this.$t('YES') },
        { value: 'NO', text: this.$t('NO') },
      ];
    },
  },
  mounted() {
    if (this.project && !this.project.bank) {
      this.$set(this.project, 'bank', { gbrStatus: GBR_STATUS.NONE });
      return;
    }
    if (this.project?.bank && this.project.bank.gbrStatus == null) {
      this.$set(this.project.bank, 'gbrStatus', GBR_STATUS.NONE);
    }
  },
};
</script>

<style scoped lang="scss">
.bank-section-grid {
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) minmax(0, 2fr);
  column-gap: 1em;
  row-gap: 1em;
  align-items: stretch;
  min-width: 0;
}

@media (min-width: 992px) {
  .bank-section-grid--paired {
    grid-template-columns:
      minmax(8rem, 1fr) minmax(0, 2fr)
      minmax(8rem, 1fr) minmax(0, 2fr);
    gap: 1em 2em;
  }
}

.bank-section-grid .label {
  text-align: left;
  padding-top: 0.4em;
  align-self: start;
}

.bank-section-grid > div:not(.label) {
  min-width: 0;
}
</style>
